import React, { useEffect, useState } from "react";
import {
    Stack,
    Text,
    ensure,
    FormValidationSummary,
    ValidationResult,
    validationFailed,
    createErrorFailure,
    ConfirmDialog
} from "sprint-common-web";

import { AncillaryContentSubtype } from "../../../../common/types/AncillaryContentType";
import { ContentSubtype } from "../../../../common/types/ContentSubtype";
import { useAssetsManageAncillaryApi } from "../../api/useAssetsManageAncillaryApi";
import { ValidateMergeAncillariesRequest } from "../../api/ValidateMergeAncillariesRequest";
import { AncillaryItem } from "../AncillaryItem";

interface MergeAncillariesConfirmationModalProps {
    ancillaryContentType: AncillaryContentSubtype;
    targetAncillary?: AncillaryItem;
    orphanedAncillary?: AncillaryItem;

    isOpen: boolean;
    isConfirmationDisabled: boolean;
    onConfirm: () => Promise<void>;
    onDismiss: () => void;
}

export const MergeAncillariesConfirmationModal = (props: MergeAncillariesConfirmationModalProps): JSX.Element => {
    const { validateMergeAncillaries } = useAssetsManageAncillaryApi();

    const [isValidationPending, setIsValidationPending] = useState(false);
    const [validationResult, setValidationResult] = useState<ValidationResult>();

    useEffect(() => {
        setValidationResult(undefined);

        if (!props.isOpen) return;

        if (isAnyAncillaryMissing(props)) return;

        const { targetAncillary, orphanedAncillary } = props;
        ensure(targetAncillary);
        ensure(orphanedAncillary);

        setIsValidationPending(true);

        validateMergeAncillaries(createRequest(targetAncillary, orphanedAncillary))
            .then((res) => {
                setValidationResult(res);
            })
            .catch((err) => {
                const failure = validationFailed([createErrorFailure(JSON.stringify(err))]);
                setValidationResult(failure);
            })
            .finally(() => setIsValidationPending(false));
    }, [props.isOpen]);

    const isAnyAncillaryMissing = ({ targetAncillary, orphanedAncillary }: MergeAncillariesConfirmationModalProps) =>
        !targetAncillary || !orphanedAncillary;

    if (isAnyAncillaryMissing(props)) return <></>;

    const isConfirmButtonDisabled = props.isConfirmationDisabled || isValidationPending || !validationResult?.isValid;

    return (
        <ConfirmDialog
            title={`Merge ${resolveAncillarySubtypeName(props.ancillaryContentType)}`}
            text="You are about to merge:"
            isBlocking={true}
            hidden={!props.isOpen}
            confirm={{ onClick: props.onConfirm, disabled: isConfirmButtonDisabled }}
            cancel={{ onClick: props.onDismiss }}
            width={800}
        >
            <Stack tokens={{ childrenGap: 10 }} styles={{ root: { minHeight: 130 } }}>
                <ConfirmationDetails target={props.targetAncillary} orphan={props.orphanedAncillary} />
                <FormValidationSummary validationResult={validationResult} isValidating={isValidationPending} />
            </Stack>
        </ConfirmDialog>
    );
};

interface ConfirmationDetailsProps {
    target?: AncillaryItem;
    orphan?: AncillaryItem;
}

const indentationStyles = { root: { textIndent: "20px" } };

const ConfirmationDetails = ({ target, orphan }: ConfirmationDetailsProps): JSX.Element => {
    if (!target || !orphan) return <></>;

    return (
        <Stack tokens={{ childrenGap: 10 }}>
            <Text styles={indentationStyles}>
                <b>{orphan.title}</b> (orphan)
            </Text>
            <Text>into</Text>
            <Text styles={indentationStyles}>
                <b>{target.title}</b> (target).
            </Text>
        </Stack>
    );
};

const createRequest = (
    targetVersion: AncillaryItem,
    orphanedVersion: AncillaryItem
): ValidateMergeAncillariesRequest => {
    return {
        targetAncillaryId: targetVersion.id,
        orphanedAncillaryId: orphanedVersion.id
    };
};

export const resolveAncillarySubtypeName = (contentType: AncillaryContentSubtype): string => {
    const ancillaryTypeResolver: Record<AncillaryContentSubtype, string> = {
        [ContentSubtype.Promo]: "Promo",
        [ContentSubtype.Safety]: "Safety",
        [ContentSubtype.Advert]: "Advert",
        [ContentSubtype.IndentWarning]: "Indent Warning",
        [ContentSubtype.ClientSpecific]: "Client Specific"
    };

    return ancillaryTypeResolver[contentType];
};
